import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from 'components/ui/Header';
import WhatsAppChat from 'components/ui/WhatsAppChat';
import CallButton from 'components/ui/CallButton';
import BookingSummaryPanel from 'components/ui/BookingSummaryPanel';
import AccommodationGallery from './components/AccommodationGallery';
import AccommodationInfo from './components/AccommodationInfo';
import BookingWidget from './components/BookingWidget';
import GuestReviews from './components/GuestReviews';
import AccommodationComparison from './components/AccommodationComparison';

const AccommodationDetails = () => {
  const location = useLocation();
  const [selectedAccommodation, setSelectedAccommodation] = useState('Deluxe Forest Villa');
  const [bookingData, setBookingData] = useState(null);
  const [showBookingSummary, setShowBookingSummary] = useState(false);

  // Handle accommodation selection from navigation state
  useEffect(() => {
    if (location?.state?.accommodation) {
      setSelectedAccommodation(location?.state?.accommodation);
    }
  }, [location?.state]);

  // Handle booking updates from BookingWidget
  const handleBookingUpdate = (data) => {
    setBookingData(data);
    setShowBookingSummary(true);
  };

  // Handle booking summary close
  const handleBookingSummaryClose = () => {
    setShowBookingSummary(false);
  };

  // Handle proceed to payment
  const handleProceedToPayment = () => {
    // This would typically navigate to payment page
    console.log('Proceeding to payment with:', bookingData);
  };

  // Accommodation data based on selection
  const getAccommodationData = (name) => {
    const accommodations = {
      'Deluxe Forest Villa': {
        name: 'Deluxe Forest Villa',
        type: 'Villa',
        maxGuests: 4,
        bedrooms: 2,
        bathrooms: 2,
        area: '850 sq ft',
        basePrice: 4500,
        available: true,
        description: `Experience luxury in the heart of nature with our Deluxe Forest Villa. This spacious accommodation offers panoramic views of the lush Dandeli forest, providing the perfect blend of comfort and wilderness adventure.\n\nThe villa features modern amenities while maintaining an authentic connection to nature. Wake up to the sounds of exotic birds and enjoy your morning coffee on the private balcony overlooking the canopy.`,
        amenities: [
          { icon: "Wifi", name: "Free Wi-Fi" },
          { icon: "Car", name: "Free Parking" },
          { icon: "Snowflake", name: "Air Conditioning" },
          { icon: "Tv", name: "Smart TV" },
          { icon: "Coffee", name: "Coffee Maker" },
          { icon: "Bath", name: "Private Bathroom" },
          { icon: "Utensils", name: "Mini Refrigerator" },
          { icon: "Shield", name: "24/7 Security" },
          { icon: "Shirt", name: "Laundry Service" },
          { icon: "Phone", name: "Room Service" },
          { icon: "TreePine", name: "Forest View" },
          { icon: "Wind", name: "Balcony/Terrace" }
        ],
        highlights: [
          "Panoramic forest views from private balcony",
          "Spacious living area with modern furnishings",
          "Premium bedding and luxury bathroom amenities",
          "Complimentary welcome drinks and local snacks"
        ],
        policies: [
          { title: "Check-in", value: "2:00 PM onwards" },
          { title: "Check-out", value: "11:00 AM" },
          { title: "Cancellation", value: "Free cancellation up to 24 hours before check-in" },
          { title: "Pets", value: "Pet-friendly (additional charges apply)" },
          { title: "Smoking", value: "Non-smoking property" },
          { title: "Age Restriction", value: "Minimum age 18 years" }
        ]
      },
      'River View Cottage': {
        name: 'River View Cottage',
        type: 'Cottage',
        maxGuests: 3,
        bedrooms: 1,
        bathrooms: 1,
        area: '650 sq ft',
        basePrice: 3800,
        available: true,
        description: `Immerse yourself in the tranquil sounds of flowing water with our River View Cottage. Located directly beside the pristine Kali River, this cozy accommodation offers an intimate connection with nature's serenity.\n\nThe cottage features a private garden area where you can relax and enjoy the gentle river breeze. Perfect for couples or small families seeking a peaceful retreat away from the hustle and bustle.`,
        amenities: [
          { icon: "Wifi", name: "Free Wi-Fi" },
          { icon: "Car", name: "Free Parking" },
          { icon: "Snowflake", name: "Air Conditioning" },
          { icon: "Coffee", name: "Coffee Maker" },
          { icon: "Bath", name: "Private Bathroom" },
          { icon: "Utensils", name: "Mini Refrigerator" },
          { icon: "Phone", name: "Room Service" },
          { icon: "Waves", name: "River View" },
          { icon: "Flower", name: "Private Garden" }
        ],
        highlights: [
          "Direct river access for morning walks",
          "Private garden space for relaxation",
          "Peaceful location away from crowds",
          "Perfect spot for bird watching"
        ],
        policies: [
          { title: "Check-in", value: "2:00 PM onwards" },
          { title: "Check-out", value: "11:00 AM" },
          { title: "Cancellation", value: "Free cancellation up to 24 hours before check-in" },
          { title: "Pets", value: "Pet-friendly (additional charges apply)" },
          { title: "Smoking", value: "Non-smoking property" },
          { title: "Age Restriction", value: "Minimum age 18 years" }
        ]
      }
    };

    return accommodations?.[name] || accommodations?.['Deluxe Forest Villa'];
  };

  const currentAccommodation = getAccommodationData(selectedAccommodation);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Gallery Section */}
              <section>
                <AccommodationGallery 
                  accommodationType={selectedAccommodation}
                />
              </section>

              {/* Accommodation Information */}
              <section>
                <AccommodationInfo 
                  accommodation={currentAccommodation}
                />
              </section>

              {/* Guest Reviews */}
              <section>
                <GuestReviews />
              </section>

              {/* Accommodation Comparison */}
              <section>
                <AccommodationComparison 
                  currentAccommodation={selectedAccommodation}
                />
              </section>
            </div>

            {/* Sidebar - Booking Widget */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                <BookingWidget 
                  accommodation={currentAccommodation}
                  onBookingUpdate={handleBookingUpdate}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Floating Action Buttons */}
      <WhatsAppChat />
      <CallButton />

      {/* Booking Summary Panel */}
      <BookingSummaryPanel
        isVisible={showBookingSummary}
        bookingData={bookingData}
        formData={bookingData}
        onClose={handleBookingSummaryClose}
        onProceed={handleProceedToPayment}
      />
    </div>
  );
};

export default AccommodationDetails;